import React from "react";
import { SlidersHorizontal, X } from "lucide-react";
import AppButton from "./AppButton";

/**
 * Shared filter controls: FilterButton + ActiveFilterChips. Pairs with FilterPanel.
 *
 * Converted to Tailwind utilities in Phase 5 (2026-08-24) together with FilterPanel —
 * FilterControls.scss is deleted (TOKEN_CONTRACT §6.1). See FilterPanel.jsx for the
 * finding the pair shares: both carried a private indigo palette (#6366f1 / #4f46e5)
 * that no institute's brand could reach. Everything here is `--primary` now.
 *
 * The chip's border was `rgba(99, 102, 241, 0.22)` and its ink indigo-600 — i.e. the
 * accent, darker, for readability on its own 10 % tint. That role has a token:
 * `--primary-soft-fg`, which Phase 7.1 derives per institute by measured contrast
 * rather than by darkening a fixed hex.
 */

/**
 * FilterButton — labeled "Filters" trigger with an active-count badge.
 * Text collapses to icon-only on very small screens.
 *
 * Props:
 *   count     — number of active filters (shows a badge when > 0)
 *   onClick    — open handler
 *   label      — button text (default "Filters")
 *   className  — extra class
 */
/* ⚠️ This IS an AppButton now, not a copy of one. It used to hand-roll the
   geometry — 36px, 8px radius, 0.875rem/500, 0.5rem gap — under a comment saying
   the values were "copied from AppButton --outline / --default", and a copied
   value is a value that drifts. Two ways it already had:

   1. `tw-bg-surface-raised` instead of the outline variant's `transparent`, so
      the trigger was a white chip on an off-white page where every Cancel and
      Reset button beside it was see-through.

   2. `tw-border` with NO `tw-border-solid`. Preflight is off, so nothing in this
      app declares a border-style — see PanelButton, which carries the same note.
      On a <button> the UA default is `outset`, so this control has been drawing
      a 1px BEVEL while `.app-btn` writes the shorthand `1px solid` and draws a
      line. That is why it read as heavier than the SearchInput next to it.

   Both are gone by construction: the button now takes its border, radius, fill,
   hover, focus ring and disabled state from the one place those are defined. */

const FilterButton = ({ count = 0, onClick, label = "Filters", className = "" }) => (
  <AppButton
    /* ⚠️ `type="button"` is not optional and AppButton has no default for it.
       A <button> inside a <form> is a SUBMIT button, and this trigger renders on
       toolbars that sit inside one. It was explicit on the markup this replaced. */
    type="button"
    /* The engaged state is a variant, not a utility layered over one — see the
       note on `outline-primary` in AppButton.jsx. `tw-relative` is layout, not
       colour, so it cannot lose the same race. */
    variant={count > 0 ? "outline-primary" : "outline"}
    className={`tw-relative tw-shrink-0 ${className}`.trim()}
    onClick={onClick}
    aria-label="Open filters"
  >
    <SlidersHorizontal size={16} />
    {/* Bootstrap's `sm` breakpoint (576px), which is what the SCSS used —
        NOT Tailwind's `sm` (640px). */}
    <span className="max-[575.98px]:tw-hidden">{label}</span>
    {count > 0 && (
      <span className="tw-absolute -tw-top-1 -tw-right-1 tw-bg-primary-solid tw-text-primary-solid-fg tw-text-[10px] tw-font-semibold tw-min-w-4 tw-h-4 tw-px-1 tw-rounded-full tw-flex tw-items-center tw-justify-center">
        {count}
      </span>
    )}
  </AppButton>
);

/**
 * ActiveFilterChips — removable chips for the currently applied filters,
 * plus a "Clear All" action. Renders nothing when there are no active filters.
 *
 * The bottom margin is internal for the same reason FilterToolbar's is: the pages
 * adopting this pattern still import SCSS, so they cannot write a `tw-` class without
 * tripping the `mixed` check. It costs nothing when there are no filters — the whole
 * component returns null.
 *
 * Props:
 *   items      — [{ key, label, onRemove }]
 *   onClearAll — clears everything (optional)
 *   className  — extra class
 */
const ActiveFilterChips = ({ items = [], onClearAll, className = "" }) => {
  if (!items.length) return null;
  return (
    <div className={`tw-flex tw-items-center tw-flex-wrap tw-gap-2 tw-mb-3 ${className}`}>
      {items.map((chip) => (
        <span
          key={chip.key}
          className="tw-inline-flex tw-items-center tw-gap-1 tw-py-[0.3rem] tw-pr-[0.35rem] tw-pl-[0.65rem] tw-rounded-full tw-bg-primary/10 tw-text-primary-soft-fg tw-border tw-border-solid tw-border-primary/20 tw-text-[0.8125rem] tw-font-medium tw-whitespace-nowrap"
        >
          {chip.label}
          <button
            type="button"
            className="tw-inline-flex tw-items-center tw-justify-center tw-w-[18px] tw-h-[18px] tw-border-0 tw-rounded-full tw-bg-transparent tw-text-inherit tw-cursor-pointer tw-transition-[background-color] hover:tw-bg-primary/20"
            onClick={chip.onRemove}
            aria-label={`Remove ${chip.label}`}
          >
            <X size={13} />
          </button>
        </span>
      ))}
      {onClearAll && (
        <button
          type="button"
          className="tw-bg-transparent tw-border-0 tw-text-content-muted tw-text-[0.8125rem] tw-font-semibold tw-cursor-pointer tw-px-[0.4rem] tw-py-[0.3rem] hover:tw-text-content hover:tw-underline"
          onClick={onClearAll}
        >
          Clear All
        </button>
      )}
    </div>
  );
};

export { FilterButton, ActiveFilterChips };
